import React, { useCallback, useRef, useEffect } from 'react';
import type { PlatformFrameProps } from '@wix/editor-platform-host-integration-apis';

const panelSizeWidth = {
  UNKNOWN_WIDTH: '100%',
  SMALL: '288px',
  MEDIUM: '404px',
  LARGE: '660px',
};

const defaultPanelHeight = '480px';

export const PlatformFramePanel: React.FC<PlatformFrameProps> = ({
  appDefinitionId,
  getPlatformInfraAPI,
  displayName,
  url,
  panelSize,
  iframeProps,
  onUnmount,
}) => {
  const frameRef = useRef<HTMLIFrameElement | null>();
  const setRef = useCallback((ref: HTMLIFrameElement | null) => {
    frameRef.current = ref;
  }, []);

  const initializeFrame = useCallback(() => {
    const isValidProps = () => appDefinitionId && frameRef.current;

    if (isValidProps()) {
      if (typeof getPlatformInfraAPI !== 'function') {
        console.warn('PlatformFrame - getWorkerManager is not a function');
      } else {
        getPlatformInfraAPI().then((infraAPI) => {
          // Double-check validity after async operation
          if (infraAPI && isValidProps()) {
            void infraAPI.connectFrame(appDefinitionId, frameRef.current!);
          }
        });
      }
    }
  }, [appDefinitionId, getPlatformInfraAPI]);

  useEffect(() => {
    initializeFrame();
  }, [initializeFrame]);

  useEffect(() => {
    return () => {
      onUnmount?.();
    };
  }, [onUnmount]);

  const panelWidth = panelSize?.width
    ? typeof panelSize?.width === 'number'
      ? `${panelSize.width}px`
      : panelSizeWidth[panelSize.width]
    : panelSizeWidth.MEDIUM;
  const panelHeight = panelSize?.height
    ? `${panelSize.height}px`
    : defaultPanelHeight;

  return (
    <iframe
      width={panelWidth}
      height={panelHeight}
      {...(iframeProps || {})}
      data-aid="platform-iframe"
      data-platform-consumer-type="panel-builder"
      ref={setRef}
      src={url}
      title={displayName || ''}
    />
  );
};
